﻿import React, { useState, useEffect, useRef } from 'react';
import { SamplePreparation } from '../../models/Analysis/sample-preparation';
import { ModalMessageType, VmiGroups, WeightMeasurementType } from '../../models/enums';
import { Asset } from '../../models/asset';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { CrudFunctions } from '../../utilities/crud-functions';
import { GlasswareLookupItem } from '../../models/glassware-lookup-item';

import DataGrid, {
    Column,
    Paging,
    Scrolling,
    Toolbar,
    Editing,
    Lookup,
    Button as ColumnButton,
    Item as ToolBarItem
} from 'devextreme-react/data-grid';

import Tabs from 'devextreme-react/tabs';

const tabsConfig = [
    {
        id: 0,
        text: 'Sample Weights',
        icon: 'product',
    },
    {
        id: 1,
        text: 'Dilutions',
        icon: 'fill',
    },
    {
        id: 2,
        text: 'pH Measurements',
        icon: 'expand',
    }
];

export enum SamplePreparationsTabType {
    Samples = 0,
    FinalDilutions = 1,
    PhMeasurements = 2
}

export interface Props {
    benchSheetId?: string;
    diluent: boolean;
    title?: string;
}

export default function SamplePreparationsComponent(props: Props) {
    let finalDilutionsDatagridRef: any = useRef(null);
    let phMeasurementsDatagridRef: any = useRef(null);

    const [selectedTab, setSelectedTab] =
        useState<SamplePreparationsTabType>(SamplePreparationsTabType.Samples);

    const [preparations, setPreparations] =
        useState<SamplePreparation[]>();

    const [balances, setBalances] =
        useState<Asset[]>();

    const [phMeters, setPhMeters] =
        useState<Asset[]>();

    const [selectedSamplePreparation, setSelectedSamplePreparation] =
        useState<SamplePreparation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, []);

    return (
        <div>

            <div style={{ display: "flex", alignItems: "center" }}>
                <div>
                    <Tabs
                        id="withText"
                        onSelectedIndexChange={onSelectedTabIndexChange}
                        defaultSelectedIndex={0}
                        dataSource={tabsConfig}
                        scrollByContent={true}
                        showNavButtons={true}
                        orientation="horizontal"
                        stylingMode="primary"
                        iconPosition="start"
                        width={600}>
                    </Tabs>
                </div>
            </div>

            {selectedTab === SamplePreparationsTabType.Samples &&
                <div className="pw-margin-top-1">
                    <DataGrid id="samplePreparationsDataGrid"
                        keyExpr="samplePreparationId"
                        height='15rem'
                        width="100%"
                        dataSource={preparations ? preparations : []}
                        onSelectionChanged={onSamplePreparationsSelectionChanged}
                        onRowValidating={onSamplePreparationsRowValidating}
                        onRowRemoving={onSamplePreparationsRowRemoving}
                        onInitNewRow={onInitNewSamplePreparation}
                        wordWrapEnabled={true}
                        showRowLines={true}
                        allowColumnResizing={true}
                        columnResizingMode={'widget'}
                        rowAlternationEnabled={true}
                        showBorders={true}>

                        <Scrolling columnRenderingMode={'virtual'} />
                        <Paging enabled={false} />

                        <Column type="buttons" width="7rem">
                            <ColumnButton name="edit" />
                            <ColumnButton name="delete" />
                        </Column>

                        <Column dataField='sampleName' caption="Sample"
                            width='6rem'
                            cssClass="table-vertical-align" />

                        <Column dataField='notes'
                            cssClass="table-vertical-align" />

                        <Column dataField='chemicalName' caption="Chemical" width='10rem'
                            cssClass="table-vertical-align" />

                        <Column dataField='internalChemicalId' caption="Chemical ID" width='10rem'
                            cssClass="table-vertical-align" />

                        <Column dataField='batchName' caption="Batch" width='10rem'
                            cssClass="table-vertical-align" />

                        <Column dataField='neat' dataType="boolean" width={"5rem"}
                            caption="No Dilution" cssClass="table-vertical-align" />

                        <Column dataField='sampleMeasurementString' caption="Measurement"
                            cellRender={measurementItemRender}
                            width='6rem' cssClass="table-vertical-align" />

                        <Column dataField='weightAsset' caption="Asset" width='7rem'
                            cssClass="table-vertical-align" />

                        <Column dataField='concentrationString' caption="Actual Conc."
                            cellRender={actualConcentrationItemRender}
                            width='8rem' cssClass="table-vertical-align" />

                        <Toolbar>
                            <ToolBarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">{props.title ? props.title : "Sample Weights"}</div>
                                </div>
                            </ToolBarItem>
                            <ToolBarItem name="addRowButton" />

                        </Toolbar>

                        <Editing mode="row"
                            newRowPosition="last"
                            useIcons={true}
                            allowAdding={true}
                            allowUpdating={true}
                            allowDeleting={true} />

                    </DataGrid>

                    {/*
                <ContextMenu dataSource={contextMenuItems}
                    width="12rem" 
                    onItemClick={onSamplePreparationsDataGridContextMenuClick}
                    target="#samplePreparationsDataGrid" />
                    */}
                </div>
            }

            {selectedTab === SamplePreparationsTabType.FinalDilutions &&
                <div className="pw-margin-top-1">
                    <DataGrid id="finalDilutionsDatagrid"
                        keyExpr="samplePreparationId" ref={finalDilutionsDatagridRef}
                        height='15rem'
                        width="100%"
                        dataSource={preparations ?
                            preparations : []}
                        onSelectionChanged={onSamplePreparationsSelectionChanged}
                        onRowValidating={onSamplePreparationsRowValidating}
                        onRowRemoving={onSamplePreparationsRowRemoving}
                        wordWrapEnabled={true}
                        showRowLines={true}
                        allowColumnResizing={true}
                        columnResizingMode={'widget'}
                        rowAlternationEnabled={true}
                        showBorders={true}>

                        <Scrolling columnRenderingMode={'virtual'} />
                        <Paging enabled={false} />

                        <Column type="buttons" width="7rem">
                            <ColumnButton name="edit" />
                            <ColumnButton name="delete" />
                        </Column>

                        <Column dataField='sampleName' caption="Sample"
                            width='6rem' allowEditing={false}
                            cssClass="table-vertical-align" />

                        <Column dataField='finalDilutionMeasurementString' caption="Final Dilution"
                            cellRender={finalVolumeItemRender}
                            width='6rem' cssClass="table-vertical-align" />

                        <Column dataField='dilutionAsset' caption="Asset"
                            width='7rem'
                            cssClass="table-vertical-align" />

                        <Column dataField='finalDilutionSolvent'
                            cellRender={finalDilutionSolventRender}
                            cssClass="table-vertical-align" />

                        <Column dataField='finalDilutionSolventId' caption="Solvent ID"
                            cellRender={finalDilutionSolventIdRender}
                            width='7rem' cssClass="table-vertical-align" />

                        <Toolbar>
                            <ToolBarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Dilutions</div>
                                </div>
                            </ToolBarItem>
                            <ToolBarItem name="addRowButton" />
                        </Toolbar>

                        <Editing mode="row"
                            useIcons={true}
                            allowAdding={true}
                            allowUpdating={true}
                            allowDeleting={true} />
                    </DataGrid>
                </div>
            }

            {selectedTab === SamplePreparationsTabType.PhMeasurements &&
                <div className="pw-margin-top-1">
                    <DataGrid id="phMeasurementsDatagrid"
                        keyExpr="samplePreparationId" ref={phMeasurementsDatagridRef}
                        height='15rem' width="100%"
                        dataSource={preparations ?
                            preparations : []}
                        onSelectionChanged={onSamplePreparationsSelectionChanged}
                        onRowValidating={onSamplePreparationsRowValidating}
                        onRowRemoving={onSamplePreparationsRowRemoving}
                        wordWrapEnabled={true}
                        showRowLines={true}
                        allowColumnResizing={true}
                        columnResizingMode={'widget'}
                        rowAlternationEnabled={true}
                        showBorders={true}>

                        <Scrolling columnRenderingMode={'virtual'} />
                        <Paging enabled={false} />

                        <Column type="buttons" width="7rem">
                            <ColumnButton name="edit" />
                            <ColumnButton name="delete" />
                        </Column>

                        <Column dataField='sampleName' caption="Sample"
                            width='6rem' allowEditing={false}
                            cssClass="table-vertical-align" />

                        <Column dataField='phMeasurementAssetId' caption="PH Meter"
                            width='10rem' cssClass="table-vertical-align" cellRender={pHMeterItemRender}  >
                            <Lookup dataSource={phMeters}
                                allowClearing={true}
                                valueExpr='assetId'
                                displayExpr='assetName' />
                        </Column>

                        <Column dataField='phMeasurementString' caption="Measurement"
                            width='6rem' cssClass="table-vertical-align" />

                        <Column dataField='phMeasurementChemical' caption="Chemical / Solution"
                            cellRender={phMeasurementChemicalRender}
                            cssClass="table-vertical-align" />

                        <Column dataField='phMeasurementChemicalId' caption="Chemical ID"
                            cellRender={phMeasurementChemicalIdRender}
                            width='7rem' cssClass="table-vertical-align" />

                        <Column dataField='phAdjustedVolume' caption="ph Adj. Volume"
                            width='9rem' cssClass="table-vertical-align" />

                        <Toolbar>
                            <ToolBarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">pH Meter Readings</div>
                                </div>
                            </ToolBarItem>
                            <ToolBarItem name="addRowButton" />
                        </Toolbar>

                        <Editing mode="row"
                            useIcons={true}
                            allowAdding={true}
                            allowUpdating={true}
                            allowDeleting={true} />
                    </DataGrid>
                </div>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div >
    );

    function onSelectedTabIndexChange(e: any) {
        setSelectedTab(e);
    }

    function onInitNewSamplePreparation(e: any) {
        let newSamplePreparation: SamplePreparation = {
            neat: false,
            benchSheetId: props.benchSheetId
        };

        e.data = newSamplePreparation;
    }

    function onSamplePreparationsSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedSamplePreparation(e.selectedRowsData[0]);
    }

    function refreshDataGrids() {
        const finalDilutionsDatagridInstance = finalDilutionsDatagridRef.current!.instance();

        if (finalDilutionsDatagridInstance)
            finalDilutionsDatagridInstance.refresh();

        const phMeasurementsDatagridInstance = phMeasurementsDatagridRef.current!.instance();

        if (phMeasurementsDatagridInstance)
            phMeasurementsDatagridInstance.refresh();
    }

    async function onSamplePreparationsRowValidating(e: any) {
        if (e.oldData) {
            let samplePreparation = e.oldData as SamplePreparation;

            if (samplePreparation) {
                let updatedSamplePreparation = { ...samplePreparation, ...e.newData };

                updateSamplePreparation(updatedSamplePreparation)
                    .then(() => {
                        getSamplePreparations();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Sample Preparation");
                    });
            }
        }
        else {
            if (e.newData) {
                let samplePreparation = e.newData as SamplePreparation;

                if (samplePreparation) {
                    samplePreparation.benchSheetId = props.benchSheetId;
                    samplePreparation.diluent = props.diluent;

                    await insertSamplePreparation(samplePreparation)
                        .then(() => {
                            getSamplePreparations();
                        })
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Inserting Sample Preparation ");
                            //have to do this - otherwise the changes that remain are viewed as updates and not inserts
                            getSamplePreparations();
                        });
                }
            }
        }
    }

    async function onSamplePreparationsRowRemoving(e: any) {
        if (e.data && e.data.samplePreparationId) {
            await deleteSamplePreparation(e.data.samplePreparationId)
                .then(() => {
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Sample Preparation");
                });
        }
    }

    async function getSamplePreparations() {
        if (props.benchSheetId)
            await CrudFunctions.getData(`benchsheets/samplepreparations/${props.benchSheetId}/${props.diluent}`)
                .then(data => {
                    setPreparations(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Sample Preparations");
                });
    }

    async function getBalances() {
        await CrudFunctions.getData("assets/allbalances")
            .then(data => {
                setBalances(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Balances");
            });
    }

    async function getPhMeters() {
        await CrudFunctions.getData(`assets/assets?vmiGroup=${VmiGroups.PhMeter}`)
            .then(data => {
                setPhMeters(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving pH Meters");
            });
    }

    async function insertSamplePreparation(samplePreparation: SamplePreparation) {
        return await CrudFunctions.postData("benchsheets/samplepreparation", samplePreparation);
    }

    async function updateSamplePreparation(samplePreparation: SamplePreparation) {
        return await CrudFunctions.putData("benchsheets/samplepreparation", samplePreparation);
    }

    async function deleteSamplePreparation(samplePreparationId: string) {
        return await CrudFunctions.deleteData(`benchsheets/samplepreparation/${samplePreparationId}`);
    }

    async function getAllData() {
        await Promise.all([
            getBalances(),
            getPhMeters(),
            getSamplePreparations()
        ])
    }


    //rendering

    /*
    function chemicalItemRender(e: any) {
        if (e.data) {
            return (
                <div>
                    <div className="item-main">{e.data.chemicalName ? e.data.chemicalName : 'NA'}</div>
                    <div className="item-sub">Lot: {e.data.chemicalLotString ? e.data.chemicalLotString : 'NA'}</div>
                </div>
            );
        }
        else
            return (<div>NA</div>);
    }
    */


    /*
    function renderDeleteButton(e: any) {
        let sample = e.data as SamplePreparation;

        if (!sample.defined) {
            return <span className="dx-link dx-icon-trash datagrid-icon column-button-margin"></span>
        }
        return <span></span>;
    }


    function renderSampleName(e: any) {
        let sample = e.data as SamplePreparation;

        if (!sample.defined) {
            return <span className="dx-link dx-icon-trash datagrid-icon column-button-margin"></span>
        }
        return <span></span>;
    }
    
    function volumetricFlaskItemRender(e: any) {
        if (e.data) {
            if (e.data.neat) {
                return (<div>NA</div>);
            }
            else if (e.data.glassware) {
                const glasswareItem = e.data.glassware;

                return (
                    <div id={`chem-${glasswareItem.glasswareItemId}`}>
                        <div className="item-main">{glasswareItem.name}</div>
                        <div className="item-sub">{glasswareItem.description}</div>
                    </div>
                );
            }
            else
                return (<div></div>);
        }
    }

    function assetItemRender(e: any) {
        if (e.data) {
            if (e.data.neat) {
                return (<div>NA</div>);
            }

            if (e.data.measurementType === WeightMeasurementType.Gravimetric) {
                if (e.data.balance) {
                    const balance = e.data.balance;

                    if (balance) {
                        return (
                            <div id={`asset-${balance.assetNumber ? balance.assetNumber : 'NA'}`}>
                                <div className="item-main">{balance.assetNumber ? balance.assetNumber : 'NA'}</div>
                                <div className="item-sub">{balance.description ? balance.description : 'NA'}</div>
                            </div>
                        );
                    }
                    else
                        return (<div></div>);
                }
                else
                    return (<div></div>);
            }
            else if (e.data.measurementType === WeightMeasurementType.Volumetric) {
                if (e.data.glassware) {
                    const glassware = e.data.glassare as GlasswareLookupItem;;

                    if (glassware) {
                        return (
                            <div id={`asset-${glassware.glasswareItemId ? glassware.glasswareItemId : 'NA'}`}>
                                <div className="item-main">{glassware.name ? glassware.name : 'NA'}</div>
                                <div className="item-sub">{glassware.description ? glassware.description : 'NA'}</div>
                            </div>
                        );
                    }
                    else
                        return (<div></div>);
                }
                else
                    return (<div></div>);
            }
        }
        return (<div></div>);
    }

    
    function balanceItemRender(e: any) {
        if (e.data) {
            if (e.data.neat) {
                return (<div>NA</div>);
            }
            if (e.data.balance) {
                const balance = e.data.balance;

                return (
                    <div id={`asset-${balance.assetNumber}`}>
                        <div className="item-main">{balance.assetNumber}</div>
                        <div className="item-sub">{balance.description}</div>
                    </div>
                );
            }
            else
                return (<div></div>);
        }
    }
    */

    function pHMeterItemRender(e: any) {
        if (e.data) {
            if (e.data.neat)
                return <div>NA</div>;
            else
                return <div>{e.displayValue}</div>;
        }
        else
            return <div></div>;
    }

    //render

    function measurementItemRender(e: any) {
        if (e.data) {
            if (e.data.neat) {
                return (<div>NA</div>);
            }
            else
                return (
                    <div>{e.data.sampleMeasurementString}</div>
                );
        }
        else
            return (<div></div>);
    }

    function finalVolumeItemRender(e: any) {
        if (e.data) {
            if (e.data.neat) {
                return (<div>NA</div>);
            }
            else
                return (
                    <div>{e.data.finalDilutionMeasurementString}</div>
                );
        }
        else
            return (<div></div>);
    }

    function finalDilutionSolventRender(e: any) {
        if (e.data) {
            if (e.data.neat) {
                return (<div>NA</div>);
            }
            else
                return (
                    <div>{e.data.finalDilutionSolvent}</div>
                );
        }
        else
            return (<div></div>);
    }

    function finalDilutionSolventIdRender(e: any) {
        if (e.data) {
            if (e.data.neat) {
                return (<div>NA</div>);
            }
            else
                return (
                    <div>{e.data.finalDilutionSolventId}</div>
                );
        }
        else
            return (<div></div>);
    }

    function phMeasurementChemicalRender(e: any) {
        if (e.data) {
            if (e.data.neat) {
                return (<div>NA</div>);
            }
            else
                return (
                    <div>{e.data.phMeasurementChemical}</div>
                );
        }
        else
            return (<div></div>);
    }

    function phMeasurementChemicalIdRender(e: any) {
        if (e.data) {
            if (e.data.neat) {
                return (<div>NA</div>);
            }
            else
                return (
                    <div>{e.data.phMeasurementChemicalId}</div>
                );
        }
        else
            return (<div></div>);
    }

    function actualConcentrationItemRender(e: any) {
        if (e.data) {
            if (e.data.neat && !e.data.overrideTargetConcentration) {
                return (<div>NA</div>);
            }
            else
                return (
                    <div>{e.data.concentrationString}</div>
                );
        }
        else
            return (<div></div>);
    }

    //other functions

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}